import { HOURS_IN_DAY, MS_PER_DAY } from '../constants.js';
import { notify } from './notify.js';

export const formatTimestamp = (isoString) => {
  return new Date(isoString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// The two halves of formatTimestamp, for listing several entries from the same
// day: the date is stated once and each entry carries only its time.
export const formatDatePart = (isoString) =>
  new Date(isoString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
export const formatTimePart = (isoString) =>
  new Date(isoString).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
// 24-hour "H:MM" for a time quoted in a sentence, e.g. "9:44", "16:00".
export const formatClockTime = (date) => {
  const d = new Date(date);
  return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// Records' day headings. The key is the local calendar day (not the UTC one an
// ISO slice would give), so a 23:30 entry doesn't land under tomorrow.
export const dayKey = (isoString) => new Date(isoString).toDateString();
export const dayHeading = (isoString) => {
  const key = dayKey(isoString);
  if (key === new Date().toDateString()) return 'Today';
  if (key === new Date(Date.now() - MS_PER_DAY).toDateString()) return 'Yesterday';
  return new Date(isoString).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

// How long ago something happened, in the coarsest unit that still reads
// precisely — "40 min" beats "0.7h" when a parent is deciding whether they can
// dose now. Rounds down so it never overstates the elapsed time.
export const formatElapsed = (date) => {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(date).getTime()) / 60000));
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
};

// How long until a future moment, in formatElapsed's style ("2h 34min").
// Rounds *up*, the opposite of formatElapsed: a wait must never read shorter
// than it is, so "in 0 min" can't show while a dose is still too soon.
export const formatRemaining = (date) => {
  const mins = Math.max(0, Math.ceil((new Date(date).getTime() - Date.now()) / 60000));
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
};

// "3 hours ago" for the Records list, where the question is usually "how long
// since?" rather than "at what o'clock?" — the wall-clock stamp is on the line
// above, so this is the reading that saves the arithmetic.
//
// Coarser than formatElapsed on purpose: that one gates a dose and needs the
// minutes ("4h 50min" vs the 5-hour wait), this one is a glance. Under an hour
// it still counts minutes, since "0 hours ago" says nothing; past a day it
// counts days, so a week-old entry doesn't read as "163 hours ago". Rounds down
// throughout, and future timestamps (a backdated entry typed wrong, a phone
// with a skewed clock) fall back to "just now" rather than a negative count.
export const formatAgo = (date) => {
  const mins = Math.floor((Date.now() - new Date(date).getTime()) / 60000);
  if (!(mins > 0)) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < HOURS_IN_DAY) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / HOURS_IN_DAY);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

export const capitalizeMed = (name) => name.charAt(0).toUpperCase() + name.slice(1);

// Format a Date as the local "YYYY-MM-DDTHH:mm" value an <input type="datetime-local">
// expects (its value is naive local time, so we shift by the tz offset first).
export const toLocalDatetimeValue = (date) => {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

// The other direction, for a time typed into one of those fields: a Date, or
// null after explaining why not. A cleared field (Android's picker has a Clear
// button) used to throw out of toISOString and leave the form stuck on
// "Recording…". A time in the future is refused too: a dose stamped later
// than now reads as "just given" and blocks the next one until that moment.
// A few minutes of slack covers two phones whose clocks disagree.
export const parseEnteredTime = (value) => {
  const d = new Date(value);
  if (!value || isNaN(d.getTime())) { notify('Enter a date and time'); return null; }
  if (d.getTime() > Date.now() + 5 * 60000) { notify('That time is in the future'); return null; }
  return d;
};

// The device nickname as it's shown in Records: bracketed, so "(Mom's phone)"
// reads as who logged the entry rather than as another of its values sitting in
// the same dot-separated line. One helper so doses and temperature readings
// can't drift apart on it. Returns undefined for an unstamped entry, which the
// filter(Boolean) callers drop.
export const loggedByLabel = (name) => (name ? `(${name})` : undefined);

export const calculateAge = (dob) => {
  const now = new Date();
  const birth = new Date(dob);
  let months = (now.getFullYear() - birth.getFullYear()) * 12
    + now.getMonth() - birth.getMonth();
  if (now.getDate() < birth.getDate()) months--;
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  if (years === 0) return `${remainingMonths}m`;
  if (remainingMonths === 0) return `${years}y`;
  return `${years}y ${remainingMonths}m`;
};

export const formatDaysAgo = (days) =>
  days === 0 ? 'today' : days === 1 ? 'yesterday'
  : days < 60 ? `${days} days ago` : `${Math.floor(days / 30.44)} months ago`;
